import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import {
  getAllChatbots,
  deleteChatbot,
  updateChatbotStatus,
} from "../apis/chatbots";
import { Bot, Plus, Settings, Trash, Loader2, ArrowLeft, MessageSquare, Calendar } from "lucide-react";
import ConfirmationModal from "../components/ConfirmationModal";

// Modern Toggle Switch Component
const ToggleSwitch = ({ checked, onChange, disabled, loading }) => {
  return (
    <div className="flex items-center gap-3">
      <motion.button
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/40 ${
          checked ? 'bg-gradient-to-r from-emerald-600 to-emerald-700' : 'bg-white/10 border border-white/10'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={onChange}
        disabled={disabled}
        whileTap={{ scale: 0.95 }}
      >
        <motion.span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
          animate={{ x: checked ? 24 : 4 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        />
        {loading && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Loader2 className="h-3 w-3 animate-spin text-white" />
          </motion.div>
        )}
      </motion.button>
      <span className={`text-sm font-medium ${checked ? 'text-emerald-300' : 'text-gray-400'}`}>
        {checked ? 'Active' : 'Inactive'}
      </span>
    </div>
  );
};

export default function ChatbotsList() {
  const { instanceId } = useParams();
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, chatbotId: null });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChatbots = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAllChatbots(instanceId);
        setChatbots(data || []);
      } catch (err) {
        setError(err.message || "Failed to load chatbots");
        console.error("Error fetching chatbots:", err);
      } finally {
        setLoading(false);
      }
    };

    if (instanceId) {
      fetchChatbots();
    }
  }, [instanceId]);

  const handleCreateNew = () => {
    navigate(`/instance/${instanceId}/chatbots/new`);
  };

  const handleManage = (chatbotId) => {
    navigate(`/instance/${instanceId}/chatbots/${chatbotId}`);
  };

  const handleDelete = (chatbotId) => {
    setDeleteConfirm({ open: true, chatbotId });
  };

  const confirmDeleteChatbot = async () => {
    const { chatbotId } = deleteConfirm;
    if (!chatbotId) return;

    try {
      setDeletingId(chatbotId);
      await deleteChatbot(chatbotId);
      setChatbots((prev) => prev.filter((bot) => bot.id !== chatbotId));
    } catch (err) {
      alert("Failed to delete chatbot. Please try again.");
      console.error("Delete chatbot error:", err);
    } finally {
      setDeletingId(null);
      setDeleteConfirm({ open: false, chatbotId: null });
    }
  };

  const toggleActiveStatus = async (chatbotId, currentStatus) => {
    const desiredStatus = currentStatus ? 0 : 1;

    setChatbots((prev) =>
      prev.map((bot) =>
        bot.id === chatbotId ? { ...bot, isActive: desiredStatus } : bot
      )
    );
    setUpdatingStatusId(chatbotId);

    try {
      await updateChatbotStatus(chatbotId, { active: desiredStatus });
    } catch (err) {
      setChatbots((prev) =>
        prev.map((bot) =>
          bot.id === chatbotId ? { ...bot, isActive: currentStatus ? 1 : 0 } : bot
        )
      );
      alert("Failed to update chatbot status. Please try again.");
      console.error("Update chatbot status error:", err);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F13]">
        <div className="flex justify-center items-center h-64">
          <motion.div
            className="flex items-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Loader2 className="w-6 h-6 animate-spin text-emerald-300" />
            <span className="text-lg font-medium text-gray-300">Loading chatbots...</span>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0F13] text-white">
        <div className="text-center p-8">
          <motion.div
            className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-8 max-w-md mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-rose-400 font-semibold text-lg mb-2">Error loading chatbots</div>
            <div className="text-gray-300 mb-6">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-3 rounded-xl hover:from-emerald-700 hover:to-emerald-800 transition-all transform hover:scale-105 shadow-lg"
            >
              Try Again
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F13] text-white">
      <div className=" mx-auto p-6">
        {/* Header Section */}
        <motion.div
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-4">
            <motion.button
              onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors p-2 rounded-xl hover:bg-white/10"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Go back"
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <div>
              <h1 className="text-3xl font-bold text-white">My Chatbots</h1>
              <p className="text-gray-300 mt-1">Manage and monitor your AI assistants</p>
            </div>
          </div>

          <motion.button
            onClick={handleCreateNew}
            className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-8 py-4 rounded-2xl hover:from-emerald-700 hover:to-emerald-800 transition-all flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={loading}
          >
            <Plus className="w-5 h-5" />
            <span className="font-semibold">Create New Chatbot</span>
          </motion.button>
        </motion.div>

        {/* Content Section */}
        <AnimatePresence mode="wait">
          {chatbots.length === 0 ? (
            <motion.div
              className="text-center py-20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <div className="bg-white/5 border border-white/10 rounded-3xl shadow-xl p-12 max-w-2xl mx-auto">
                <motion.div
                  className="bg-white/10 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
                  animate={{ 
                    rotate: [0, 5, -5, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  <Bot className="w-12 h-12 text-emerald-300" />
                </motion.div>
                <h2 className="text-3xl font-bold text-white mb-4">No chatbots yet</h2>
                <p className="text-gray-300 text-lg mb-8">Create your first chatbot to get started with AI-powered conversations</p>
                <motion.button
                  onClick={handleCreateNew}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-8 py-4 rounded-2xl hover:from-emerald-700 hover:to-emerald-800 transition-all shadow-lg hover:shadow-xl font-semibold"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Create Your First Chatbot
                </motion.button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              className="grid gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {chatbots.map((chatbot, index) => (
                <motion.div
                  key={chatbot.id}
                  className="group relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div
                    className={`rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border overflow-hidden relative ${
                      chatbot.isActive === 1
                        ? 'bg-white/5 border-emerald-400/30 hover:border-emerald-300/50'
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                  >
                    {/* Active Status Indicator */}
                    {chatbot.isActive === 1 && (
                      <div className="absolute top-0 right-0 w-32 h-32 -mr-16 -mt-16">
                        <div className="absolute inset-0 bg-emerald-500 rounded-full opacity-10 animate-pulse"></div>
                      </div>
                    )}

                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                      {/* Chatbot Info */}
                      <div className="flex items-start gap-6 flex-1">
                        <motion.div
                          className={`p-4 rounded-2xl bg-white/10`}
                          whileHover={{ scale: 1.05, rotate: 5 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          <Bot className={`w-8 h-8 ${chatbot.isActive === 1 ? 'text-emerald-300' : 'text-gray-400'}`} />
                        </motion.div>

                        <div className="flex-1 min-w-0">
                          <h3 className="text-2xl font-bold text-white mb-2 truncate">
                            {chatbot.bot_name}
                          </h3>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-gray-300 mb-3">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>Created {new Date(chatbot.created_at).toLocaleDateString()}</span>
                            </div>
                            {chatbot.initial_message && (
                              <div className="flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" />
                                <span className="truncate max-w-xs">
                                  {chatbot.initial_message.slice(0, 50)}...
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Status Badge */}
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              chatbot.isActive === 1
                                ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-400/30'
                                : 'bg-white/10 text-gray-300 border border-white/10'
                            }`}>
                              {chatbot.isActive === 1 ? 'Online' : 'Offline'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Controls */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        {/* Toggle Switch */}
                        <ToggleSwitch
                          checked={chatbot.isActive === 1}
                          onChange={() => toggleActiveStatus(chatbot.id, chatbot.isActive === 1)}
                          disabled={updatingStatusId === chatbot.id}
                          loading={updatingStatusId === chatbot.id}
                        />

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3">
                          <motion.button
                            onClick={() => handleManage(chatbot.id)}
                            className="bg-gradient-to-r from-sky-600 to-sky-700 text-white px-6 py-3 rounded-xl hover:from-sky-700 hover:to-sky-800 transition-all flex items-center gap-2 shadow-lg hover:shadow-xl font-medium"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            aria-label={`Manage ${chatbot.bot_name}`}
                          >
                            <Settings className="w-4 h-4" />
                            <span>Manage</span>
                          </motion.button>

                          <motion.button
                            onClick={() => handleDelete(chatbot.id)}
                            className="bg-gradient-to-r from-rose-600 to-rose-700 text-white px-6 py-3 rounded-xl hover:from-rose-700 hover:to-rose-800 transition-all flex items-center gap-2 shadow-lg hover:shadow-xl font-medium"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            disabled={deletingId === chatbot.id}
                            aria-label={`Delete ${chatbot.bot_name}`}
                          >
                            {deletingId === chatbot.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash className="w-4 h-4" />
                            )}
                            <span>Delete</span>
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, chatbotId: null })}
        onConfirm={confirmDeleteChatbot}
        title="Delete Chatbot"
        message="Are you sure you want to delete this chatbot? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}
